// Account create / edit form — live preview of the computed display_name as
// the user picks customer/tester/product_type/last4. Server is authoritative
// on save.
//
// Sections: Customer Profile · Account Details · Account Access · Payment
// Methods · Linked Accounts · Account Data Linkage. Every account rolls up
// to a customer profile (mig 0017): pick customer → platform → profile, or
// add a new profile in place. Contact + OTP details live on the profile;
// the tester comes from it unless the platform's profiles are shared.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { selectClass } from '@/components/ui/field';
import { accounts, ApiError, customerPlatforms, customerProfiles, customers, lookups, testers } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import { LinkedAccountsPanel } from './linked-accounts';
import { formatAddress, platformLabel } from '@frt/shared-types';
import type { AccountCreate, AccountStatus, TillerStatus } from '@frt/shared-types';
import { Field, testerLabel } from '@/components/ui/field';
import { ProfileFormDialog, modeLabel } from '../profiles/profile-form';
import { OobTable } from '../profiles/pages';

type YesNo = '' | 'yes' | 'no';

const NEW_PROFILE = -1;

export function AccountFormPage({ mode }: { mode: 'create' | 'edit' }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: idStr } = useParams({ strict: false }) as { id?: string };
  const id = mode === 'edit' ? Number(idStr) : null;
  const { data: me } = useMe();

  const { data: customerList } = useQuery({
    queryKey: ['customers', { per_page: 500 }],
    queryFn: () => customers.list({ per_page: 500 }),
  });
  const { data: testerList } = useQuery({
    queryKey: ['testers', { per_page: 500 }],
    queryFn: () => testers.list({ per_page: 500 }),
  });
  const { data: productTypeList } = useQuery({
    queryKey: ['lookups', 'product-types'],
    queryFn: () => lookups.productTypes.list(),
  });
  const { data: accountTypeList } = useQuery({
    queryKey: ['lookups', 'account-types'],
    queryFn: () => lookups.accountTypes.list(),
  });
  // Category dropdown was removed — Category lives on FRT Customer (mig 0009).
  const { data: paymentStatusOptions } = useQuery({
    queryKey: ['lookups', 'payment-status-options'],
    queryFn: () => lookups.paymentStatusOptions.list(),
  });
  const { data: existing } = useQuery({
    queryKey: ['account', id],
    queryFn: () => accounts.get(id!),
    enabled: mode === 'edit' && Number.isFinite(id),
  });

  const [form, setForm] = useState({
    frt_customer_id: 0,
    tester_id: 0,
    account_type_id: 0,
    product_type_id: 0,
    frt_category_id: 0,
    on_point_tester_id: 0,
    account_number_last4: '',
    tiller_status: 'new' as TillerStatus,
    account_status: 'active' as AccountStatus,
    accounts_held: '',
    legacy_account_id: '',
    notes: '',
    // ISO date string (YYYY-MM-DD) — empty when unset.
    date_opened: '',
    // Yes/No tri-state: '' = unspecified (NULL), 'yes' = true, 'no' = false.
    debit_card_available: '' as YesNo,
    checks_available: '' as YesNo,
    in_bitwarden: '' as YesNo,
    // mig 0010 — Tester Relationship + per-account contract terms
    tester_relationship: 'internal' as 'third_party' | 'internal',
    contract_signed: '' as YesNo,
    contract_term_months: '' as string,
    contract_signed_date: '',
    frt_tester_fee: '' as string,
    payment_status_id: '' as number | '',
    payment_date: '',
    // mig 0017 — the customer profile this account rolls up to
    customer_platform_id: 0,
    customer_profile_id: 0,
  });
  const [showErrors, setShowErrors] = useState(false);
  const [newProfileOpen, setNewProfileOpen] = useState(false);

  const { data: platformList } = useQuery({
    queryKey: ['customer-platforms', form.frt_customer_id],
    queryFn: () => customerPlatforms.forCustomer(form.frt_customer_id),
    enabled: form.frt_customer_id > 0,
  });
  const { data: profileList } = useQuery({
    queryKey: ['customer-profiles', { customer_platform_id: form.customer_platform_id }],
    queryFn: () =>
      customerProfiles.list({ customer_platform_id: form.customer_platform_id, active: 'all' }),
    enabled: form.customer_platform_id > 0,
  });
  const { data: selectedProfile } = useQuery({
    queryKey: ['customer-profile', form.customer_profile_id],
    queryFn: () => customerProfiles.get(form.customer_profile_id),
    enabled: form.customer_profile_id > 0,
  });
  const platform = platformList?.data.find((p) => p.id === form.customer_platform_id);
  const sharedProfile = (selectedProfile?.profile_mode ?? platform?.profile_mode) === 'shared';

  // A per-tester profile decides the account's tester.
  useEffect(() => {
    if (selectedProfile && selectedProfile.tester_id && selectedProfile.tester_id !== form.tester_id) {
      setForm((f) => ({ ...f, tester_id: selectedProfile.tester_id! }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProfile?.id]);

  useEffect(() => {
    if (existing) {
      setForm({
        frt_customer_id: existing.frt_customer_id,
        tester_id: existing.tester_id,
        account_type_id: existing.account_type_id,
        product_type_id: existing.product_type_id,
        frt_category_id: existing.frt_category_id ?? 0,
        on_point_tester_id: existing.on_point_tester_id ?? 0,
        account_number_last4: existing.account_number_last4,
        tiller_status: existing.tiller_status,
        account_status: existing.account_status,
        accounts_held: existing.accounts_held ?? '',
        legacy_account_id: existing.legacy_account_id ?? '',
        notes: existing.notes ?? '',
        date_opened: existing.date_opened ?? '',
        debit_card_available: boolToYn(existing.debit_card_available),
        checks_available: boolToYn(existing.checks_available),
        in_bitwarden: boolToYn(existing.in_bitwarden),
        tester_relationship: existing.tester_relationship ?? 'internal',
        contract_signed: boolToYn(existing.contract_signed),
        contract_term_months:
          existing.contract_term_months !== null && existing.contract_term_months !== undefined
            ? String(existing.contract_term_months)
            : '',
        contract_signed_date: existing.contract_signed_date ?? '',
        frt_tester_fee:
          existing.frt_tester_fee !== null && existing.frt_tester_fee !== undefined
            ? String(existing.frt_tester_fee)
            : '',
        payment_status_id: existing.payment_status_id ?? '',
        payment_date: existing.payment_date ?? '',
        customer_platform_id: existing.customer_profile?.customer_platform_id ?? 0,
        customer_profile_id: existing.customer_profile_id ?? 0,
      });
    }
  }, [existing]);

  // live preview of display_name
  const previewName = useMemo(() => {
    const c = customerList?.data.find((x) => x.id === form.frt_customer_id);
    const t = testerList?.data.find((x) => x.id === form.tester_id);
    const p = productTypeList?.data.find((x) => x.id === form.product_type_id);
    if (!c || !t || !p || !form.account_number_last4) return '';
    const last4 = form.account_number_last4.padStart(4, '0');
    if (t.kind === 'person') {
      if (!t.short_name) return '(tester missing short_name)';
      return `${c.code}-${t.short_name}-${p.code}-${last4}`;
    }
    if (!t.short_business_name) return '(tester missing short_business_name)';
    if (!t.opened_by_initials) return '(tester missing opened_by_initials)';
    return `${c.code}-${t.short_business_name}-${t.opened_by_initials}-${p.code}-${last4}`;
  }, [form, customerList, testerList, productTypeList]);

  const testerRows = testerList?.data ?? [];
  const profileErrors: { profile?: string; tester?: string } = {};
  if (!form.customer_profile_id) profileErrors.profile = 'Select the online profile';
  if (sharedProfile && !form.tester_id) profileErrors.tester = 'Select the tester';
  const hasErrors = Object.keys(profileErrors).length > 0;

  function buildBody(): AccountCreate {
    return {
      customer_profile_id: form.customer_profile_id,
      // Customer always, and the tester unless shared, come from the profile.
      ...(sharedProfile ? { tester_id: form.tester_id } : {}),
      account_type_id: form.account_type_id,
      product_type_id: form.product_type_id,
      frt_category_id: form.frt_category_id || null,
      on_point_tester_id: form.on_point_tester_id || null,
      account_number_last4: form.account_number_last4.padStart(4, '0'),
      tiller_status: form.tiller_status,
      account_status: form.account_status,
      accounts_held: form.accounts_held || null,
      legacy_account_id: form.legacy_account_id || null,
      notes: form.notes || null,
      date_opened: form.date_opened || null,
      debit_card_available: ynToBool(form.debit_card_available),
      checks_available: ynToBool(form.checks_available),
      in_bitwarden: ynToBool(form.in_bitwarden),
      tester_relationship: form.tester_relationship,
      contract_signed: ynToBool(form.contract_signed),
      contract_term_months:
        form.contract_term_months && Number.isFinite(Number(form.contract_term_months))
          ? Number(form.contract_term_months)
          : null,
      contract_signed_date: form.contract_signed_date || null,
      frt_tester_fee:
        form.frt_tester_fee && Number.isFinite(Number(form.frt_tester_fee))
          ? Number(form.frt_tester_fee)
          : null,
      payment_status_id: form.payment_status_id || null,
      payment_date: form.payment_date || null,
    };
  }

  const create = useMutation({
    mutationFn: () => accounts.create(buildBody()),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void navigate({ to: '/accounts/$id', params: { id: String(created.id) } });
    },
  });

  const update = useMutation({
    mutationFn: () => accounts.update(id!, buildBody()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['account', id] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  const error = create.error ?? update.error;
  const perr = showErrors ? profileErrors : {};

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">
        {mode === 'create' ? 'New Account' : 'Edit Account'}
      </h1>
      <Card>
        <CardHeader>
          <CardTitle>
            Display name preview:{' '}
            <span className="font-mono text-brand-700">{previewName || '—'}</span>
          </CardTitle>
        </CardHeader>
        <CardBody>
          <form
            className="space-y-6"
            onSubmit={(e) => {
              e.preventDefault();
              setShowErrors(true);
              if (hasErrors) return;
              if (mode === 'create') create.mutate();
              else update.mutate();
            }}
          >
            {/* ---------------- Online Profile ---------------- */}
            <Section
              title="Online Profile"
              hint="The profile holds the address, phone, email and OTP channels; the account rolls up to it."
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field label="FRT Customer *">
                  <select
                    value={form.frt_customer_id}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        frt_customer_id: Number(e.target.value),
                        customer_platform_id: 0,
                        customer_profile_id: 0,
                      })
                    }
                    className={selectClass}
                  >
                    <option value={0}>— select —</option>
                    {(customerList?.data ?? [])
                      // Non-customer FIs exist only for linked accounts (mig 0014).
                      .filter((c) => c.is_customer || c.id === form.frt_customer_id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.code} ({c.name})
                        </option>
                      ))}
                  </select>
                </Field>
                <Field label="Platform *">
                  <select
                    value={form.customer_platform_id}
                    disabled={!form.frt_customer_id}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        customer_platform_id: Number(e.target.value),
                        customer_profile_id: 0,
                      })
                    }
                    className={selectClass}
                  >
                    <option value={0}>{form.frt_customer_id ? '— select —' : 'Pick a customer first'}</option>
                    {(platformList?.data ?? [])
                      .filter((p) => p.active || p.id === form.customer_platform_id)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {platformLabel({ name: p.name, type: p.platform_type_name })} ·{' '}
                          {modeLabel(p.profile_mode)}
                        </option>
                      ))}
                  </select>
                  {form.frt_customer_id > 0 && platformList && platformList.data.length === 0 && (
                    <span className="block text-xs text-amber-700 mt-0.5">
                      No platforms yet — add one on the{' '}
                      <Link
                        to="/customers/$id"
                        params={{ id: String(form.frt_customer_id) }}
                        target="_blank"
                        rel="noreferrer"
                        className="underline"
                      >
                        customer’s page
                      </Link>
                      .
                    </span>
                  )}
                </Field>
                <Field label="Online profile *" error={perr.profile}>
                  <select
                    value={form.customer_profile_id}
                    disabled={!form.customer_platform_id}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (v === NEW_PROFILE) setNewProfileOpen(true);
                      else setForm({ ...form, customer_profile_id: v });
                    }}
                    className={selectClass}
                  >
                    <option value={0}>
                      {form.customer_platform_id ? '— select —' : 'Pick a platform first'}
                    </option>
                    {(profileList?.data ?? [])
                      .filter((p) => p.active || p.id === form.customer_profile_id)
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.tester_display ?? 'Shared'}
                          {p.label ? ` · ${p.label}` : ''}
                          {p.active ? '' : ' (inactive)'}
                        </option>
                      ))}
                    {form.customer_platform_id > 0 && (
                      <option value={NEW_PROFILE}>+ New profile…</option>
                    )}
                  </select>
                </Field>
              </div>
              {selectedProfile && (
                <div className="rounded bg-slate-50 px-3 py-2 text-sm">
                  <Link
                    to="/profiles/$id"
                    params={{ id: String(selectedProfile.id) }}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {selectedProfile.display}
                  </Link>
                  <span className="text-slate-600">
                    {' · '}
                    {selectedProfile.phone_display ?? 'no phone'} · {selectedProfile.email ?? 'no email'}
                    {' · '}
                    {selectedProfile.oob_entries.length} OTP channel
                    {selectedProfile.oob_entries.length === 1 ? '' : 's'}
                    {formatAddress(selectedProfile) ? ` · ${formatAddress(selectedProfile)}` : ''}
                  </span>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label={sharedProfile ? 'Tester *' : 'Tester (from the profile)'}
                  error={perr.tester}
                >
                  <select
                    value={form.tester_id}
                    disabled={!sharedProfile}
                    onChange={(e) => setForm({ ...form, tester_id: Number(e.target.value) })}
                    className={selectClass}
                  >
                    <option value={0}>—</option>
                    {testerRows.map((t) => (
                      <option key={t.id} value={t.id}>
                        {testerLabel(t)}
                      </option>
                    ))}
                  </select>
                </Field>
                <label className="block text-sm">
                  <span className="block text-slate-600">On point tester</span>
                  <select
                    value={form.on_point_tester_id}
                    onChange={(e) =>
                      setForm({ ...form, on_point_tester_id: Number(e.target.value) })
                    }
                    className={selectClass}
                  >
                    <option value={0}>—</option>
                    {testerRows.map((t) => (
                      <option key={t.id} value={t.id}>
                        {testerLabel(t)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {/* Tester Relationship + per-account contract terms (mig 0010) */}
              <div className="space-y-3 pt-1">
                <label className="block text-sm">
                  <span className="block text-slate-600">Tester Relationship</span>
                  <select
                    value={form.tester_relationship}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        tester_relationship: e.target.value as 'third_party' | 'internal',
                      })
                    }
                    className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                  >
                    <option value="internal">Internal</option>
                    <option value="third_party">Third Party</option>
                  </select>
                </label>
                {form.tester_relationship === 'third_party' && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="block text-sm">
                      <span className="block text-slate-600">Contract Signed</span>
                      <YesNoSelect
                        value={form.contract_signed}
                        onChange={(v) => setForm({ ...form, contract_signed: v })}
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="block text-slate-600">Contract Term (months)</span>
                      <Input
                        type="number"
                        min={1}
                        value={form.contract_term_months}
                        onChange={(e) =>
                          setForm({ ...form, contract_term_months: e.target.value })
                        }
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="block text-slate-600">
                        Contract Date {form.contract_signed === 'yes' && '*'}
                      </span>
                      <Input
                        type="date"
                        value={form.contract_signed_date}
                        onChange={(e) =>
                          setForm({ ...form, contract_signed_date: e.target.value })
                        }
                        required={form.contract_signed === 'yes'}
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="block text-slate-600">FRT Tester Fee ($)</span>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={form.frt_tester_fee}
                        onChange={(e) => setForm({ ...form, frt_tester_fee: e.target.value })}
                      />
                    </label>
                    <label className="block text-sm">
                      <span className="block text-slate-600">Payment Status</span>
                      <select
                        value={form.payment_status_id}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            payment_status_id: e.target.value ? Number(e.target.value) : '',
                          })
                        }
                        className={selectClass}
                      >
                        <option value="">—</option>
                        {(paymentStatusOptions?.data ?? [])
                          .filter((o) => o.active)
                          .map((o) => (
                            <option key={o.id} value={o.id}>
                              {o.name}
                            </option>
                          ))}
                      </select>
                    </label>
                    <label className="block text-sm">
                      <span className="block text-slate-600">
                        Payment Date{' '}
                        {paymentStatusOptions?.data.find(
                          (o) => o.id === form.payment_status_id,
                        )?.name === 'Paid' && '*'}
                      </span>
                      <Input
                        type="date"
                        value={form.payment_date}
                        onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                        required={
                          paymentStatusOptions?.data.find(
                            (o) => o.id === form.payment_status_id,
                          )?.name === 'Paid'
                        }
                      />
                    </label>
                  </div>
                )}
              </div>
            </Section>

            {/* ---------------- Account Details ---------------- */}
            <Section title="Account Details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="block text-slate-600">Account type *</span>
                  <select
                    value={form.account_type_id}
                    onChange={(e) =>
                      setForm({ ...form, account_type_id: Number(e.target.value) })
                    }
                    required
                    className={selectClass}
                  >
                    <option value={0}>— select —</option>
                    {(accountTypeList?.data ?? []).map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} ({a.name})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Product type *</span>
                  <select
                    value={form.product_type_id}
                    onChange={(e) =>
                      setForm({ ...form, product_type_id: Number(e.target.value) })
                    }
                    required
                    className={selectClass}
                  >
                    <option value={0}>— select —</option>
                    {(productTypeList?.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.code} ({p.name})
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Account number last 4 *</span>
                  <Input
                    value={form.account_number_last4}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        account_number_last4: e.target.value.replace(/[^0-9]/g, '').slice(0, 4),
                      })
                    }
                    required
                    className="font-mono"
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Date opened</span>
                  <Input
                    type="date"
                    value={form.date_opened}
                    onChange={(e) => setForm({ ...form, date_opened: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Account status</span>
                  <select
                    value={form.account_status}
                    onChange={(e) =>
                      setForm({ ...form, account_status: e.target.value as AccountStatus })
                    }
                    className={selectClass}
                  >
                    <option value="active">active</option>
                    <option value="suppressed">suppressed</option>
                    <option value="closed">closed</option>
                    <option value="converted_personal">converted_personal</option>
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Accounts held</span>
                  <Input
                    value={form.accounts_held}
                    onChange={(e) => setForm({ ...form, accounts_held: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Legacy ID</span>
                  <Input
                    value={form.legacy_account_id}
                    onChange={(e) => setForm({ ...form, legacy_account_id: e.target.value })}
                  />
                </label>
                {/* Category was moved to FRT Customer (mig 0009). Edit it on the
                    customer detail page; it propagates through the customer
                    relationship. */}
              </div>
            </Section>

            {/* ---------------- Account Access ---------------- */}
            <Section
              title="Account Access"
              hint="OTP channels are set on the online profile (edit them there)."
            >
              {selectedProfile ? (
                <OobTable entries={selectedProfile.oob_entries} />
              ) : (
                <p className="text-sm text-slate-500">Select an online profile to see its OTP channels.</p>
              )}
              <label className="block text-sm w-48">
                <span className="block text-slate-600">In Bitwarden</span>
                <YesNoSelect
                  value={form.in_bitwarden}
                  onChange={(v) => setForm({ ...form, in_bitwarden: v })}
                />
              </label>
            </Section>

            {/* ---------------- Payment Methods ---------------- */}
            <Section title="Payment Methods">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="block text-slate-600">Debit card available</span>
                  <YesNoSelect
                    value={form.debit_card_available}
                    onChange={(v) => setForm({ ...form, debit_card_available: v })}
                  />
                </label>
                <label className="block text-sm">
                  <span className="block text-slate-600">Checks available</span>
                  <YesNoSelect
                    value={form.checks_available}
                    onChange={(v) => setForm({ ...form, checks_available: v })}
                  />
                </label>
              </div>
            </Section>

            {/* ---------------- Linked Accounts ---------------- */}
            <Section
              title="Linked Accounts"
              hint="Other accounts linked to this one (e.g. visible for external transfers in this bank's account center). Optional; changes here save immediately."
            >
              {mode === 'edit' && id ? (
                <LinkedAccountsPanel
                  accountId={id}
                  canEdit
                  isAdmin={me?.role === 'admin'}
                />
              ) : (
                <p className="text-sm text-slate-500">
                  Create the account first; linked accounts can then be added here or on its
                  detail page.
                </p>
              )}
            </Section>

            {/* ---------------- Account Data Linkage ---------------- */}
            <Section title="Account Data Linkage">
              <label className="block text-sm w-48">
                <span className="block text-slate-600">Tiller status</span>
                <select
                  value={form.tiller_status}
                  onChange={(e) =>
                    setForm({ ...form, tiller_status: e.target.value as TillerStatus })
                  }
                  className={selectClass}
                >
                  <option value="new">new</option>
                  <option value="integrated">integrated</option>
                  <option value="manual">manual</option>
                  <option value="login_issues">login_issues</option>
                </select>
              </label>
            </Section>

            <label className="block text-sm">
              <span className="block text-slate-600">Notes</span>
              <textarea
                className="block w-full rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />
            </label>
            <div className="flex items-center gap-3 flex-wrap">
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {mode === 'create' ? 'Create' : 'Save'}
              </Button>
              {showErrors && hasErrors && (
                <span className="text-sm text-red-600">Fix the highlighted fields above.</span>
              )}
              {update.isSuccess && !update.isPending && (
                <span className="text-sm text-green-700">Saved.</span>
              )}
              {error && <DuplicateOrErrorMessage error={error} />}
            </div>
          </form>
        </CardBody>
      </Card>
      <ProfileFormDialog
        open={newProfileOpen}
        onClose={() => setNewProfileOpen(false)}
        preset={{
          frt_customer_id: form.frt_customer_id,
          customer_platform_id: form.customer_platform_id,
          tester_id: form.tester_id || null,
        }}
        onSaved={(p) => setForm((f) => ({ ...f, customer_profile_id: p.id }))}
      />
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-slate-200 pt-4 first:border-t-0 first:pt-0">
      <div>
        <h2 className="text-base font-semibold text-slate-800">{title}</h2>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function YesNoSelect({ value, onChange }: { value: YesNo; onChange: (v: YesNo) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as YesNo)}
      className={selectClass}
    >
      <option value="">—</option>
      <option value="yes">Yes</option>
      <option value="no">No</option>
    </select>
  );
}


// Convert tri-state Yes/No to API boolean | null and back.
function ynToBool(v: YesNo): boolean | null {
  return v === 'yes' ? true : v === 'no' ? false : null;
}
function boolToYn(v: boolean | null | undefined): YesNo {
  return v === true ? 'yes' : v === false ? 'no' : '';
}

// Render either a friendly duplicate-account message (with a link to the
// existing account) when the API returns DUPLICATE_ACCOUNT or
// DUPLICATE_TILLER_LINK, or a plain red error string otherwise.
function DuplicateOrErrorMessage({ error }: { error: unknown }): React.JSX.Element {
  if (error instanceof ApiError && (error.code === 'DUPLICATE_ACCOUNT' || error.code === 'DUPLICATE_TILLER_LINK')) {
    const details = error.details as
      | { existing_account_id?: number; existing_display_name?: string; existing_account_status?: string }
      | undefined;
    const id = details?.existing_account_id;
    const name = details?.existing_display_name;
    const status = details?.existing_account_status;
    return (
      <span className="text-sm text-red-600">
        {error.message}
        {id ? (
          <>
            {' '}
            <Link
              to="/accounts/$id"
              params={{ id: String(id) }}
              className="text-brand-700 hover:underline font-mono"
            >
              {name ?? `account#${id}`}
            </Link>
            {status && status !== 'active' ? (
              <span className="text-slate-500"> ({status})</span>
            ) : null}
          </>
        ) : null}
      </span>
    );
  }
  return <span className="text-sm text-red-600">{(error as Error).message}</span>;
}
